// Les prêts à l'emploi d'Arceau (D-179, point 2) : visibles de tous les clubs dans la bibliothèque, lancés tels quels sans
// rien enregistrer ; « Garder » en fait une copie à soi, modifiable. Ils vivent dans le code, pas en base.
// Au départ : les 3 séquences et 4 des fractionnés qui existent déjà dans les éditeurs (D-162, D-098), plus 3 circuits de
// tir et 2 échauffements écrits pour ce chantier.
// À RELIRE PAR UN ENTRAÎNEUR avant l'ouverture (comme les 45 cartes du Parcours) : les circuits de tir et les échauffements
// ci-dessous (`aRelire: true`) n'ont été relus par personne ; leurs durées et leurs consignes sont des propositions.
import {
  INTERVAL_MODELS, SEQ_MODELS, defaultInterval, defaultReaction,
  type CircuitConfig, type DrillKind, type Stage,
} from './drills';
import type { Usage } from './drill-usage';

export interface Pret {
  /** `pret:<clé>` : jamais un identifiant de la base. */
  id: string;
  key: string;
  title: string;
  kind: DrillKind;
  usage: Usage;
  /** Ce qu'il fait, en une ligne (la liste, la fiche). */
  text: string;
  config: Record<string, unknown>;
  /** Écrit pour ce chantier : à relire par un entraîneur avant l'ouverture. */
  aRelire?: boolean;
}

export const PRET_PREFIX = 'pret:';
export const isPretId = (id: string | null | undefined) => !!id && id.startsWith(PRET_PREFIX);

// --- Circuits : les étapes ---------------------------------------------------------------------------------------
const cd = (id: string, label: string, seconds: number, rest_s: number): Stage =>
  ({ id, type: 'countdown', label, seconds, beep_start: true, beep_last3: true, advance: 'auto', rest_s });
const sig = (id: string, label: string, seconds: number, stimuli: Stage['stimuli'], length_ms: number, delay_ms: number, rest_s: number): Stage =>
  ({ id, type: 'signals', label, seconds, stimuli, length_ms, delay_ms, from: null, advance: 'auto', rest_s });
const circuit = (stages: Stage[]): CircuitConfig => ({ stages, auto: true, voice: true });

// À relire par un entraîneur : les trois circuits de tir.
const TOUR_DU_MONDE = circuit([
  cd('t1', 'Coin droit : tire, reprends ton rebond', 40, 10), cd('t2', 'Aile droite : tire, reprends ton rebond', 40, 10),
  cd('t3', 'Axe : tire, reprends ton rebond', 40, 10), cd('t4', 'Aile gauche : tire, reprends ton rebond', 40, 10),
  cd('t5', 'Coin gauche : tire, reprends ton rebond', 40, 0),
]);
const LF_FATIGUE = circuit([
  cd('l1', 'Aller-retour en sprint', 25, 5), cd('l2', '2 lancers francs', 30, 10),
  cd('l3', 'Aller-retour en sprint', 25, 5), cd('l4', '2 lancers francs', 30, 10),
  cd('l5', 'Aller-retour en sprint', 25, 5), cd('l6', '2 lancers francs', 30, 10),
  cd('l7', 'Aller-retour en sprint', 25, 5), cd('l8', '2 lancers francs', 30, 0),
]);
const TIR_SIGNAL = circuit([
  sig('s1', 'La flèche dit où recevoir et tirer', 60, { arrows: ['left', 'up', 'right'] }, 1500, 4500, 30),
  sig('s2', 'La flèche dit où recevoir et tirer, à 3 points', 60, { arrows: ['left', 'up', 'right'] }, 1500, 4500, 0),
]);
// À relire par un entraîneur : les deux échauffements.
const ECHAUFFEMENT = circuit([
  cd('e1', 'Course légère, tout le terrain', 60, 10), cd('e2', 'Talons-fesses', 30, 10), cd('e3', 'Montées de genoux', 30, 10),
  cd('e4', 'Pas chassés, face au panier', 30, 10), cd('e5', 'Accélérations sur la largeur', 30, 0),
]);
const ECHAUFFEMENT_BALLON = circuit([
  cd('b1', 'Dribble sur place, main droite', 30, 5), cd('b2', 'Dribble sur place, main gauche', 30, 10),
  sig('b3', 'Dribble vers la flèche', 45, { arrows: ['left', 'up', 'right', 'down'] }, 1500, 1000, 15),
  cd('b4', 'Passes à deux, en mouvement', 60, 0),
]);

const reactionSeq = (i: number) => ({ ...defaultReaction(), mode: 'sequence', sequence: SEQ_MODELS[i]!.make() });
const interval = (key: string) => ({ ...defaultInterval(key) });

const BASE: Omit<Pret, 'id'>[] = [
  { key: 'echauffement', title: 'Échauffement dynamique', kind: 'circuit', usage: 'echauffement', aRelire: true,
    text: 'Course, talons-fesses, genoux, pas chassés, accélérations : 3 min 40.', config: { ...ECHAUFFEMENT } },
  { key: 'echauffement-ballon', title: 'Échauffement ballon', kind: 'circuit', usage: 'echauffement', aRelire: true,
    text: 'Dribble des deux mains, la flèche donne la direction, passes à deux.', config: { ...ECHAUFFEMENT_BALLON } },
  { key: 'depart', title: SEQ_MODELS[0]!.name, kind: 'reaction', usage: 'reaction', text: SEQ_MODELS[0]!.text, config: reactionSeq(0) },
  { key: 'couleur', title: SEQ_MODELS[1]!.name, kind: 'reaction', usage: 'reaction', text: SEQ_MODELS[1]!.text, config: reactionSeq(1) },
  { key: 'systeme', title: SEQ_MODELS[2]!.name, kind: 'reaction', usage: 'lecture', text: SEQ_MODELS[2]!.text, config: reactionSeq(2) },
  { key: 'tour-du-monde', title: 'Tour du monde', kind: 'circuit', usage: 'tir', aRelire: true,
    text: '5 positions, 40 s chacune : tu tires, tu reprends ton rebond.', config: { ...TOUR_DU_MONDE } },
  { key: 'lf-fatigue', title: 'Lancers francs sous fatigue', kind: 'circuit', usage: 'tir', aRelire: true,
    text: 'Un aller-retour en sprint, puis 2 lancers francs : quatre fois.', config: { ...LF_FATIGUE } },
  { key: 'tir-signal', title: 'Tir sur signal', kind: 'circuit', usage: 'tir', aRelire: true,
    text: 'La flèche dit où recevoir : gauche, axe ou droite ; puis à 3 points.', config: { ...TIR_SIGNAL } },
  ...(['tabata', 'hiit-court', 'sprint', 'hiit-long'] as const).map((k): Omit<Pret, 'id'> => {
    const m = INTERVAL_MODELS.find(x => x.key === k)!;
    return { key: k, title: m.name, kind: 'fractionne', usage: 'physique', text: `${m.cfg.cycles} fois ${m.cfg.work_s} s d’effort, ${m.cfg.rest_s} s de récup.`, config: interval(k) };
  }),
];
/** Les prêts à l'emploi, dans l'ordre de leurs rayons. */
export const PRETS: Pret[] = BASE.map(p => ({ ...p, id: `${PRET_PREFIX}${p.key}` }));

export const pretById = (id: string): Pret | null => PRETS.find(p => p.id === id || p.key === id) ?? null;
